import type { Board, Pos } from "./core/types";
import {
  generateBoard,
  shuffleBoard,
  isBoardEmpty,
  hasAnyValidMove,
  findMatchInBoard,
  getGroup,
  getMaxSteps,
  applySlideMove,
  findGrabbedMatch,
  computeHint,
  levelConfig,
} from "./core";
import type { ThemePack } from "./theme";
import { getThemePack, maxKinds, tileVisual, DEFAULT_THEME_ID } from "./theme";
import { GameView } from "./render";
import { attachInput } from "./input/pointer";
import { delay } from "./render/fx";
import { hapticSuccess, hapticFailure, hapticWin } from "./util/haptics";
import { loadLevel, saveLevel, loadBoard, saveBoard, clearBoard } from "./util/progress";

/** HUD 桥接：Game 通过它更新界面文字与提示，具体实现由 UI 层提供。 */
export interface HudBridge {
  setLevel(level: number): void;
  setInfo(text: string): void;
  showToast(msg: string, kind?: "error" | "info"): void;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  origin: Pos;
  moved: boolean;
  dir: "H" | "V" | null;
  group: Pos[];
  moveVec: { x: number; y: number } | null;
  maxSteps: number;
}

const DIR_THRESHOLD = 12; // 判定滑动方向所需的最小位移(px)

export class Game {
  private board: Board = [];
  private level = loadLevel(); // 从本地恢复进度，刷新页面不丢
  private pack: ThemePack;

  private drag: DragState | null = null;
  private busy = false; // 动画序列进行中，屏蔽新输入

  constructor(
    private readonly view: GameView,
    private readonly hud: HudBridge,
    themeId: string = DEFAULT_THEME_ID,
  ) {
    this.pack = getThemePack(themeId);
  }

  async start(mount: HTMLElement): Promise<void> {
    await this.view.init(mount, this.pack);
    attachInput(this.view, this);
    this.initLevel();
  }

  // —— 关卡 ——
  private initLevel(): void {
    const cfg = levelConfig(this.level, maxKinds(this.pack));
    this.hud.setLevel(this.level);
    this.hud.setInfo(`🎨 元素种类 ${cfg.typeCount} · ${cfg.width}×${cfg.height}`);

    // 优先恢复本关的存档棋盘（刷新页面回到关卡中途的那一盘）
    const saved = loadBoard({ level: this.level, ...cfg });
    if (saved) {
      this.board = saved;
      this.view.renderBoard(this.board);
      this.checkDeadlock(); // 存档恰好是死局时照常自动洗牌
      return;
    }

    let board = generateBoard(cfg.width, cfg.height, cfg.typeCount, cfg.filled);
    // 生成的布局不保证有解，最多重试若干次直到活棋
    let tries = 0;
    while (!hasAnyValidMove(board) && tries++ < 60) {
      board = generateBoard(cfg.width, cfg.height, cfg.typeCount, cfg.filled);
    }
    this.board = board;
    saveBoard(this.level, this.board);
    this.view.renderBoard(this.board);
  }

  // —— 主题 ——
  setTheme(themeId: string): void {
    this.pack = getThemePack(themeId);
    this.view.setTheme(this.pack);
  }

  currentThemeId(): string {
    return this.pack.id;
  }

  // —— 输入（由 input/pointer 调用） ——
  beginDrag(cell: Pos, clientX: number, clientY: number, pointerId: number): boolean {
    if (this.busy || this.drag) return false;
    if (this.board[cell.y]?.[cell.x] == null) return false;
    this.view.suspendLayout = true;
    this.drag = {
      pointerId,
      startX: clientX,
      startY: clientY,
      origin: { ...cell },
      moved: false,
      dir: null,
      group: [],
      moveVec: null,
      maxSteps: 0,
    };
    return true;
  }

  moveDrag(clientX: number, clientY: number, pointerId: number): void {
    const d = this.drag;
    if (!d || d.pointerId !== pointerId) return;
    const dx = clientX - d.startX;
    const dy = clientY - d.startY;

    if (!d.dir && (Math.abs(dx) > DIR_THRESHOLD || Math.abs(dy) > DIR_THRESHOLD)) {
      d.dir = Math.abs(dx) > Math.abs(dy) ? "H" : "V";
      d.moved = true;
      const mvX = d.dir === "H" ? (dx > 0 ? 1 : -1) : 0;
      const mvY = d.dir === "V" ? (dy > 0 ? 1 : -1) : 0;
      d.moveVec = { x: mvX, y: mvY };
      d.group = getGroup(d.origin.x, d.origin.y, mvX, mvY, this.board);
      d.maxSteps = getMaxSteps(d.group, mvX, mvY, this.board);
    }

    if (d.dir && d.moveVec && d.group.length) {
      const raw = d.dir === "H" ? dx : dy;
      const sign = d.dir === "H" ? d.moveVec.x : d.moveVec.y;
      const maxPx = d.maxSteps * this.view.stepSize;
      const clamped =
        sign > 0 ? Math.max(0, Math.min(raw, maxPx)) : Math.min(0, Math.max(raw, -maxPx));
      this.view.dragOffset(d.group, d.dir === "H" ? clamped : 0, d.dir === "V" ? clamped : 0);
    }
  }

  async endDrag(clientX: number, clientY: number, pointerId: number): Promise<void> {
    const d = this.drag;
    if (!d || d.pointerId !== pointerId) return;
    this.drag = null;
    this.busy = true;

    const offset = d.dir === "H" ? clientX - d.startX : clientY - d.startY;
    const step = this.view.stepSize;

    if (d.moved && Math.abs(offset) > step * 0.2 && d.group.length && d.moveVec) {
      const steps = Math.min(Math.round(Math.abs(offset) / step), d.maxSteps);
      if (steps > 0) {
        await this.finalizeSlide(d.group, d.moveVec.x, d.moveVec.y, steps);
      } else {
        await this.view.resetGroup(d.group);
      }
    } else {
      const eliminated = this.tapEliminate(d.origin.x, d.origin.y);
      if (!eliminated && d.group.length) await this.view.resetGroup(d.group);
    }

    // 棋盘已在上面同步提交，消除特效在后台非阻塞播放；此处即可解锁输入。
    this.view.suspendLayout = false;
    // 通关过场与死局洗牌都是异步长动画，各自持有 busy 直到自己收尾；
    // 命中任一分支时不能在这里抢先解锁，否则动画期间会误放行输入。
    if (this.checkWin()) return;
    if (this.checkDeadlock()) return;
    this.busy = false;
  }

  // —— 走法结算 ——
  private async finalizeSlide(
    group: Pos[],
    dx: number,
    dy: number,
    steps: number,
  ): Promise<boolean> {
    await this.view.animateSlide(group, dx, dy, steps);
    const moved = applySlideMove(this.board, group, dx * steps, dy * steps);
    const gx = group[0].x + dx * steps;
    const gy = group[0].y + dy * steps;
    const match = findGrabbedMatch(moved, gx, gy);
    if (match) {
      this.board = moved;
      this.view.renderBoard(this.board);
      this.eliminatePair(match.a, match.b); // 非阻塞：特效在后台播放
      return true;
    }
    // 滑动无效：动画式退回原位
    hapticFailure();
    await this.view.resetGroup(group);
    return false;
  }

  private tapEliminate(x: number, y: number): boolean {
    const val = this.board[y][x];
    if (val === null) return false;
    const mate = findMatchInBoard(this.board, x, y, val);
    if (mate) {
      this.eliminatePair({ x, y }, mate); // 非阻塞：特效在后台播放
      return true;
    }
    hapticFailure();
    void this.view.shake();
    return false;
  }

  // 立即提交棋盘并重绘（网格随即可再操作），消除特效作为非阻塞覆盖层播放，
  // 因此播放消除动画期间不会锁住输入 —— 可以马上滑动下一个方块。
  private eliminatePair(a: Pos, b: Pos): void {
    const kind = this.board[a.y][a.x] ?? 0;
    const color = tileVisual(this.pack, kind).tint ?? this.pack.beamColor ?? 0x38bdf8;
    // 先把这两块摘到特效层（否则 renderBoard 会连它们一起销毁，就没有淡出动画了）
    const ghosts = [this.view.takeTile(a.x, a.y), this.view.takeTile(b.x, b.y)];
    this.board[a.y][a.x] = null;
    this.board[b.y][b.x] = null;
    saveBoard(this.level, this.board); // 每次消除后存盘，刷新页面不丢关内进度
    hapticSuccess();
    this.view.renderBoard(this.board);
    void this.view.playEliminate(a, b, color, ghosts).catch(() => {});
  }

  // —— 提示 ——
  // 只高亮「可以消除的那两块」，不弹 toast —— 避免提示文案挡住高亮的卡片。
  // pair：两块可直接消除；slide：抓起 a 推向 b 即可消除，高亮这一对足以引导。
  hint(): void {
    if (this.busy || this.drag) return;
    const h = computeHint(this.board);
    if (h.type === "pair" || h.type === "slide") {
      this.view.highlight([h.a, h.b]);
    } else {
      void this.reshuffle();
    }
  }

  // —— 死局 / 洗牌 / 通关 ——
  // 返回是否触发了洗牌（触发时 reshuffle 会自行持有并释放 busy）。
  private checkDeadlock(): boolean {
    if (isBoardEmpty(this.board)) return false; // 空盘是通关过场，不算死局
    if (!hasAnyValidMove(this.board)) {
      this.hud.showToast("⚠️ 死局 · 自动洗牌 ⚠️", "error");
      void this.reshuffle();
      return true;
    }
    return false;
  }

  private async reshuffle(): Promise<void> {
    this.busy = true;
    await delay(300);
    let board = shuffleBoard(this.board);
    let tries = 0;
    while (!hasAnyValidMove(board) && tries++ < 60) {
      board = shuffleBoard(board);
    }
    this.board = board;
    saveBoard(this.level, this.board);
    // 翻牌动画呈现洗牌过程（位置不变、牌面重排），播完后棋盘已与数据对齐。
    await this.view.playShuffle(board);
    this.busy = false;
  }

  // 返回是否通关（通关时持有 busy 直到过场结束、下一关生成完毕再释放）。
  private checkWin(): boolean {
    if (!isBoardEmpty(this.board)) return false;
    this.level++;
    saveLevel(this.level); // 持久化进度：刷新页面回到当前关卡
    clearBoard(); // 旧关棋盘作废，下一关重新生成
    hapticWin();
    this.hud.showToast(`🎉 完美通关！进入 LEVEL ${this.level} 🎉`, "info");
    this.busy = true;
    setTimeout(() => {
      this.initLevel();
      this.busy = false;
    }, 1400);
    return true;
  }
}
